/**
 * Personal Gap Analysis Constants
 * Contains category mappings, gap zone definitions, and color configurations
 */

/**
 * Map category keys to actual question category names (26 personal categories)
 */
export const PERSONAL_CATEGORY_MAP = {
  'advisor_relationships': 'advisor_relationships',
  'biblical_wisdom': 'biblical_wisdom',
  'business_stewardship': 'business_stewardship',
  'character_integrity': 'character_integrity',
  'emotional_mental': 'emotional_mental',
  'estate_planning': 'estate_planning',
  'eternal_impact': 'eternal_impact',
  'exit_vision': 'exit_vision',
  'family_communication': 'family_communication',
  'family_relationships': 'family_relationships',
  'hobbies_interests': 'hobbies_interests',
  'insurance_risk': 'insurance_risk',
  'legacy_planning': 'legacy_planning',
  'life_transition': 'life_transition',
  'lifestyle_resource': 'lifestyle_resource',
  'liquidity_cash': 'liquidity_cash',
  'marriage_family': 'marriage_family',
  'personal_financial': 'personal_financial',
  'personal_spiritual': 'personal_spiritual',
  'physical_health': 'physical_health',
  'purpose_identity': 'purpose_identity',
  'relational_health': 'relational_health',
  'social_connection': 'social_connection',
  'tax_planning': 'tax_planning',
  'time_management': 'time_management',
  'wealth_management': 'wealth_management'
};

/**
 * Gap zone definitions with labels, colors, and score ranges
 */
export const GAP_ZONES = {
  'no-gaps': {
    label: 'No Gaps',
    color: '#a07d5a',
    range: '>86%',
    scoreRange: '86-100%',
    minScore: 86,
    maxScore: 100
  },
  'minor-gaps': {
    label: 'Minor Gaps',
    color: '#6ba86e',
    range: '72-86%',
    scoreRange: '72-86%',
    minScore: 72,
    maxScore: 86
  },
  'considerable-gaps': {
    label: 'Considerable Gaps',
    color: '#6ca5d6',
    range: '57-72%',
    scoreRange: '57-72%',
    minScore: 57,
    maxScore: 72
  },
  'critical-gaps': {
    label: 'Critical Gaps',
    color: '#d4c563',
    range: '43-57%',
    scoreRange: '43-57%',
    minScore: 43,
    maxScore: 57
  },
  'very-critical-gaps': {
    label: 'Very Critical Gaps',
    color: '#d9a066',
    range: '28-43%',
    scoreRange: '28-43%',
    minScore: 28,
    maxScore: 43
  },
  'extremely-critical': {
    label: 'Extremely Critical',
    color: '#d97373',
    range: '0-28%',
    scoreRange: '0-28%',
    minScore: 0,
    maxScore: 28
  }
};

/**
 * Gap zone order for display purposes
 */
export const GAP_ZONE_ORDER = [
  'no-gaps',
  'minor-gaps',
  'considerable-gaps',
  'critical-gaps',
  'very-critical-gaps',
  'extremely-critical'
];

/**
 * Score color mappings for text display
 */
export const SCORE_TEXT_COLORS = {
  'no-gaps': 'text-[#8b6f47]',
  'minor-gaps': 'text-green-700',
  'considerable-gaps': 'text-blue-700',
  'critical-gaps': 'text-yellow-700',
  'very-critical-gaps': 'text-orange-700',
  'extremely-critical': 'text-red-700'
};

/**
 * Score badge color mappings
 */
export const SCORE_BADGE_COLORS = {
  'no-gaps': 'bg-[#f5e6d3] text-[#8b6f47] border-[#c49e73]',
  'minor-gaps': 'bg-green-100 text-green-800 border-green-300',
  'considerable-gaps': 'bg-blue-100 text-blue-800 border-blue-300',
  'critical-gaps': 'bg-yellow-100 text-yellow-800 border-yellow-300',
  'very-critical-gaps': 'bg-orange-100 text-orange-800 border-orange-300',
  'extremely-critical': 'bg-red-100 text-red-900 border-red-400'
};

/**
 * Dark colors for score cards
 */
export const SCORE_DARK_COLORS = {
  'no-gaps': '#8b6f47',
  'minor-gaps': '#2d5a2f',
  'considerable-gaps': '#2c5282',
  'critical-gaps': '#8b7500',
  'very-critical-gaps': '#a0622d',
  'extremely-critical': '#991b1b'
};

/**
 * Light background colors for score cards
 */
export const SCORE_LIGHT_BG_COLORS = {
  'no-gaps': '#f5f0e8',
  'minor-gaps': '#e6f4e7',
  'considerable-gaps': '#e3f2fd',
  'critical-gaps': '#fef9e7',
  'very-critical-gaps': '#fff3e6',
  'extremely-critical': '#fee2e2'
};

/**
 * Category background gradient colors based on score
 */
export const CATEGORY_BACKGROUND_GRADIENTS = {
  'no-gaps': 'bg-gradient-to-r from-[#d4c1a3] via-[#e8d4bb] to-gray-50',
  'minor-gaps': 'bg-gradient-to-r from-green-200 via-green-100 to-gray-50',
  'considerable-gaps': 'bg-gradient-to-r from-blue-200 via-blue-100 to-gray-50',
  'critical-gaps': 'bg-gradient-to-r from-yellow-200 via-yellow-100 to-gray-50',
  'very-critical-gaps': 'bg-gradient-to-r from-orange-200 via-orange-100 to-gray-50',
  'extremely-critical': 'bg-gradient-to-r from-red-200 via-red-100 to-gray-50'
};

/**
 * Assessment type configurations
 */
export const ASSESSMENT_TYPES = {
  'personal financial': {
    label: 'Personal Financial',
    required: true,
    questionCount: 85,
    estimatedMinutes: 43,
    description: 'Assess your financial readiness, wealth management, estate planning, tax planning, and insurance coverage.'
  },
  'personal wellbeing': {
    label: 'Personal Wellbeing',
    required: false,
    questionCount: 74,
    estimatedMinutes: 37,
    description: 'Evaluate your family relationships, marriage health, communication patterns, social connections, and support networks.'
  },
  'personal spiritual': {
    label: 'Personal Spiritual',
    required: false,
    questionCount: 30,
    estimatedMinutes: 15,
    description: 'Examine your faith journey, biblical wisdom, eternal perspective, legacy planning, and spiritual stewardship.'
  }
};

/**
 * Category display names mapping (26 personal assessment categories)
 */
export const CATEGORY_DISPLAY_NAMES = {
  'personal_financial': 'Personal Financial Goals & Planning',
  'wealth_management': 'Wealth Management & Investment Strategy',
  'estate_planning': 'Estate Planning & Document Structure',
  'tax_planning': 'Tax Planning & Efficiency',
  'insurance_risk': 'Insurance & Risk Protection',
  'advisor_relationships': 'Advisory Team & Professional Relationships',
  'legacy_planning': 'Legacy Planning & Charitable Intent',
  'liquidity_cash': 'Liquidity & Cash Flow Management',
  'family_communication': 'Family Financial Governance & Communication',
  'lifestyle_resource': 'Post-Exit Lifestyle & Resource Alignment',
  'physical_health': 'Physical Health & Vitality',
  'relational_health': 'Relational Health & Marriage',
  'family_relationships': 'Family Relationships & Legacy',
  'emotional_mental': 'Emotional & Mental Wellbeing',
  'purpose_identity': 'Purpose & Identity Beyond Business',
  'time_management': 'Time Management & Rhythm',
  'hobbies_interests': 'Hobbies, Interests & Personal Growth',
  'social_connection': 'Social Connection & Community',
  'exit_vision': 'Post-Exit Vision & Planning',
  'life_transition': 'Life Transitions & Change Readiness',
  'personal_spiritual': 'Personal Spiritual Foundation',
  'biblical_wisdom': 'Biblical Wisdom & Decision-Making',
  'marriage_family': 'Marriage & Family Spiritual Leadership',
  'business_stewardship': 'Business as Ministry & Stewardship',
  'character_integrity': 'Character, Integrity & Health',
  'eternal_impact': 'Eternal Impact & Crisis Readiness'
};

/**
 * Fixed category order: Financial → Wellbeing → Spiritual
 * This ensures consistent display order regardless of backend response order
 */
export const CATEGORY_ORDER = [
  // Personal Financial (10 categories)
  'personal_financial',
  'wealth_management',
  'estate_planning',
  'tax_planning',
  'insurance_risk',
  'advisor_relationships',
  'legacy_planning',
  'liquidity_cash',
  'family_communication',
  'lifestyle_resource',
  // Personal Wellbeing (10 categories)
  'physical_health',
  'relational_health',
  'family_relationships',
  'emotional_mental',
  'purpose_identity',
  'time_management',
  'hobbies_interests',
  'social_connection',
  'exit_vision',
  'life_transition',
  // Personal Spiritual (6 categories)
  'personal_spiritual',
  'biblical_wisdom',
  'marriage_family',
  'business_stewardship',
  'character_integrity',
  'eternal_impact'
];

/**
 * Assessment type groupings for bracket indicators
 * Note: Keys match backend naming ('personal relational'), but we alias 'personal wellbeing' to it
 */
export const ASSESSMENT_TYPE_GROUPS = {
  'personal financial': {
    categories: [
      'personal_financial',
      'wealth_management',
      'estate_planning',
      'tax_planning',
      'insurance_risk',
      'advisor_relationships',
      'legacy_planning',
      'liquidity_cash',
      'family_communication',
      'lifestyle_resource'
    ],
    label: 'Personal Financial',
    color: 'bg-blue-100 border-blue-400 text-blue-700'
  },
  'personal relational': {
    categories: [
      'physical_health',
      'relational_health',
      'family_relationships',
      'emotional_mental',
      'purpose_identity',
      'time_management',
      'hobbies_interests',
      'social_connection',
      'exit_vision',
      'life_transition'
    ],
    label: 'Personal Wellbeing',
    color: 'bg-green-100 border-green-400 text-green-700'
  },
  // Alias 'personal wellbeing' to 'personal relational' for frontend compatibility
  'personal wellbeing': {
    categories: [
      'physical_health',
      'relational_health',
      'family_relationships',
      'emotional_mental',
      'purpose_identity',
      'time_management',
      'hobbies_interests',
      'social_connection',
      'exit_vision',
      'life_transition'
    ],
    label: 'Personal Wellbeing',
    color: 'bg-green-100 border-green-400 text-green-700'
  },
  'personal spiritual': {
    categories: [
      'personal_spiritual',
      'biblical_wisdom',
      'marriage_family',
      'business_stewardship',
      'character_integrity',
      'eternal_impact'
    ],
    label: 'Personal Spiritual',
    color: 'bg-purple-100 border-purple-400 text-purple-700'
  }
};
